import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, Signal, WritableSignal, signal } from '@angular/core';
//import { AddEvent, CancelEvent, EditEvent, RemoveEvent, SaveEvent } from '@progress/kendo-angular-listview';
import { toObservable } from '@angular/core/rxjs-interop';
import { IUser, IWorkspace, PaymentService } from '../services/payment.service';
import { BehaviorSubject, combineLatest, forkJoin, lastValueFrom, map, Observable, Subject, takeUntil } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CreateFormGroupArgs, DataItem, GridDataResult, GridItem, SaveEvent } from '@progress/kendo-angular-grid';
import { MsalHelperService } from '../services/msal-helper.service';
import { AccountInfo } from '@azure/msal-browser';

const USER_TYPE_ACCOUNT = "admin";

@Component({
    selector: 'app-user-license',
    templateUrl: './user-license.component.html',
    styleUrl: './user-license.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class UserLicenseComponent implements OnDestroy {
  // public users = [
  //   { name: 'User 1', license: 'License A' },
  //   { name: 'User 2', license: 'License B' },
  // ];
  private destroy$ = new Subject<void>();
  public users$ = new BehaviorSubject<IUser[]>([]);
  public workspace$ = new BehaviorSubject<IWorkspace>(null);
  public error: WritableSignal<string> = signal("");
  public ready: WritableSignal<boolean> = signal(false);
  public user: WritableSignal<AccountInfo | null> = signal(null);
  public license$ = new BehaviorSubject(null);

  public pro$: Observable<number> = this.users$.pipe(
    map((users)=>{
      return (users || []).filter(user=>user.license_pro).length
    })
  );
  public teams$: Observable<number> = this.users$.pipe(
    map((users)=>{
      return (users || []).filter(user=>user.license_teams).length
    })
  );
  public usertypes = [USER_TYPE_ACCOUNT, "user"];

  public formGroup: FormGroup = this.formBuilder.group({
    title: "",
    user_type: "",
    license_teams: null,
    license_teams_assigned: null,
    license_pro: null,
    license_pro_assigned: null
  });
  public releaseDate: string;

  constructor(
    private msalHelperService: MsalHelperService,
    public paymentService: PaymentService,
    private formBuilder: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {
    this.createFormGroup = this.createFormGroup.bind(this);

    this.paymentService.subscription$.pipe(
      map((subscription)=>{
        this.users$.next(subscription?.users);

        const [first] = subscription?.workspace || [];
        this.workspace$.next(first);

        this.ready.set(true);
      }),
      takeUntil(this.destroy$)
    ).subscribe();

    combineLatest([this.users$, toObservable(this.user)]).pipe(

      map(([users, user])=>{

        if (!users || !user || !users.length) {
          return;
        }

        const [user_row] = (users || []).filter( (u => u.title.toLowerCase() == user.username.toLowerCase() ));

        if (user_row) {
          if (user_row.invitation == 'invited') {
            this.license$.next(user_row.invitation);
          }
          else {
            this.license$.next(user_row.user_type);
          }
        }

      }),
      takeUntil(this.destroy$)
    ).subscribe();
    
    this.user.set(this.msalHelperService.getUser());

    let d = new Date();
    d.setDate(d.getDate() - 30);
    this.releaseDate = d.toISOString();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // private currentRowIndex: number;
  // private currentUser: any;

  public createFormGroup(args: CreateFormGroupArgs): FormGroup {
    const item = args.isNew ? {} : args.dataItem;

    let users = [];
    const gridDataResult = args?.sender?.data as GridDataResult;
    if (gridDataResult) {
      users = gridDataResult.data;
    }

    const teams = (users || []).filter(user=>user.license_teams).length
    const pros = (users || []).filter(user=>user.license_pro).length
    const workspace = this.workspace$.value;

    let group: any = {
      title: [item.title, [Validators.required, Validators.email]],
      user_type: [{
        value: item.user_type,
        disabled: item.user_type == USER_TYPE_ACCOUNT && users.filter((u)=>u.user_type == USER_TYPE_ACCOUNT).length == 1
      }, [Validators.required]],
      license_pro: [{
        value: item.license_pro, 
        disabled: (item.license_pro_assigned && item.license_pro && item.license_pro_assigned > this.releaseDate) || (!item.license_pro && workspace?.seats_pro <= pros)
      }],
      license_teams: [{
        value: item.license_teams,
        disabled: (item.license_teams_assigned && item.license_teams && item.license_teams_assigned > this.releaseDate) || (!item.license_teams && workspace?.seats_teams <= teams)
      }],
    };
    // if (item.license_pro_assigned == null || item.license_pro == null || new Date(item.license_pro_assigned) < this.releaseDate) {
    //   group = {
    //     ...group,
    //     license_pro: item.license_pro
    //   }
    // }
    // if (item.license_teams_assigned == null || item.license_teams == null || new Date(item.license_teams_assigned) < this.releaseDate) {
    //   group = {
    //     ...group,
    //     license_teams: item.license_teams
    //   }
    // }

    this.formGroup = this.formBuilder.group(group);

    return this.formGroup;
  }

  public trackByIndex(index: number, item: GridItem): any {
    return index;
  }

  public onSave(event: SaveEvent) {

    // let users = this.users$.value;
    // // this.users$.next([]);
    // users = [ ...users];

    const users = this.users$.value;

    //this.users$.next([]);

    setTimeout(()=>{
      this.users$.next(users);
      this.cdr.markForCheck();
      this.onUpdate(null);
    });


    // this.users$.next(users);
    // this.cdr.markForCheck();

    event.sender.closeRow(event.rowIndex);
  }

  public onUpdate(event) {

    this.ready.set(false);
    const ws = this.workspace$.value;    

    const users = (this.users$.value || []).map((user)=>{
      return {
        email: user.title,
        user_type: user.user_type,
        license_pro: user.license_pro,
        license_teams: user.license_teams,
      }
    });

    this.paymentService.updateUsers(ws.title, users).then((response)=>{

      if (response.error) {
        this.error.set(response.error);
        return;
      }

      this.paymentService.verify(false, true);

    }).catch((reason)=>{
      if (reason.error) {
        this.error.set(reason.error.error);
      }
    }).finally(()=>{
      this.ready.set(true);
    });

  }

  public onBuy(event) {

  }

  public onJoin(event) {

    this.ready.set(false);
    const user = this.user();

    const users1 = (this.users$.value || [])
    .filter( (u => u.title.toLowerCase() == user.username.toLowerCase() ));

    const [user_row] = users1;    
    
    const users = users1.map((user)=>{
      return {
        email: user.title,
        user_type: user.user_type,
        license_pro: user.license_pro,
        license_teams: user.license_teams,
        invitation: 'joined'
      }
    });

    // give additional join argument - this doesn't remove users, just accepts 'invited'
    this.paymentService.updateUsers(user_row?.workspace, users, true).then((response)=>{

      if (response.error) {
        this.error.set(response.error);
        return;
      }

      this.paymentService.verify(false, true);

    }).catch((reason)=>{
      if (reason.error) {
        this.error.set(reason.error.error);
      }
    }).finally(()=>{
      this.ready.set(true);
    });

  }

  // public licenseProEditable(dataItem: DataItem) : boolean {
  //   return dataItem['license_pro_assigned'] == null || this.releaseDate < new Date(dataItem['license_pro_assigned']);
  // }

  // public licenseTeamsEditable(dataItem: DataItem) : boolean {
  //   return dataItem['license_teams_assigned'] == null || this.releaseDate < new Date(dataItem['license_teams_assigned']);
  // }

}
